import {
  CaseDto,
  DataBlockDictionaryItemDto,
  DataType,
  PatchCaseDto,
  PatchPatientDto,
  PatchVisitDto,
  PatientDto,
  QueryFactory,
  SexType,
  VisitDto,
} from '@lmt/shared-types';
import { format } from 'date-fns';
import { upperCaseFirstLetter } from './common';
import { getEnumNameByValue } from '../patients/list/common/types';
import {
  CustomDictionaryName,
  useDictionaryValuesByTitle,
} from '../../api/custom-hooks';
import { useMemo } from 'react';
import {
  IHardcodedField,
  caseHardcodedFields,
  patientHardcodedFields,
  visitHardcodedFields,
} from './hardcoded-fields';
/*
 * Merges `case.Values` and `case.*` fields (with `case.*` fields uppercased).
 */
export function useCaseValuesForDataBlock(caseId: number | undefined | null) {
  const caseQuery = QueryFactory.CaseQuery.useGetCaseQuery(
    { id: caseId! },
    {
      enabled: !!caseId,
      select: caseToCustomValues,
    },
  );
  return caseQuery.data;
}

/*
 * Merges `visit.Values` and `visit.*` fields (with `visit.*` fields uppercased).
 */
export function useVisitValuesForDataBlock(visitId: number | undefined | null) {
  const visitQuery = QueryFactory.AppointmentEventsQuery.useGetVisitQuery(
    { id: visitId! },
    {
      enabled: !!visitId,
      select: visitToCustomValues,
    },
  );

  return visitQuery.data;
}

let _sexDictionaryItems: DataBlockDictionaryItemDto[] = [];
/*
 * Merges `patient.Values` and `patient.*` fields (with `patient.*` fields uppercased).
 */
export function usePatientValuesForDataBlock(
  patientId: number | undefined | null,
) {
  const sexDictionaryQuery = useDictionaryValuesByTitle(
    CustomDictionaryName.Sex,
  );
  _sexDictionaryItems = sexDictionaryQuery.data?.data ?? [];
  const patientQuery = QueryFactory.PatientQuery.useGetPatientQuery(
    { id: patientId! },
    {
      enabled: !!patientId,
      select: patientToCustomValues,
    },
  );

  const result = useMemo(() => {
    const sex = patientQuery.data?.['Sex'];
    if (typeof sex === 'string') {
      if (!sexDictionaryQuery.isFetched) {
        return;
      }
      patientQuery.data['Sex'] = sexDictionaryQuery.data?.data?.find(
        (x) => x.value === patientQuery.data['Sex'],
      )?.id;
    }
    return patientQuery.data;
  }, [patientQuery.data, sexDictionaryQuery]);
  return result;
}

function patientToCustomValues(patient: PatientDto | undefined) {
  return objectToCustomValues(patient, patientHardcodedFields);
}
export function customValuesToPatient(data: any): PatchPatientDto {
  return customValuesToObject(data, patientHardcodedFields);
}

export function caseToCustomValues(caseDto: CaseDto | undefined) {
  return objectToCustomValues(caseDto, caseHardcodedFields);
}
export function customValuesToCase(data: any): PatchCaseDto {
  return customValuesToObject(data, caseHardcodedFields);
}

function visitToCustomValues(visit: VisitDto | undefined) {
  return objectToCustomValues(visit, visitHardcodedFields);
}
export function customValuesToVisit(data: any): PatchVisitDto {
  return customValuesToObject(data, visitHardcodedFields);
}

function convertSexDictionaryIdToEnum(
  sexDictionaryId: number | undefined | null,
): SexType | null | undefined {
  if (sexDictionaryId === undefined || sexDictionaryId === null)
    return SexType.Undefined;
  const sexLocalizationKey = _sexDictionaryItems.find(
    (x) => x.id === sexDictionaryId,
  )?.value;
  return convertSexLocalizationKeyToEnum(sexLocalizationKey);
}

function convertSexLocalizationKeyToEnum(
  sexLocalizationKey: string | undefined,
): SexType | null | undefined {
  if (!sexLocalizationKey) return SexType.Undefined;
  const key = sexLocalizationKey.replace('dictionaries.sex_types.', '');
  const uppercasedKey = upperCaseFirstLetter(key);
  return (
    (SexType[uppercasedKey as any] as unknown as SexType) ?? SexType.Undefined
  );
}

function convertSexToLocalizationKey(sex: SexType | undefined) {
  if (!sex) return undefined;
  return (
    'dictionaries.sex_types.' +
    getEnumNameByValue(SexType, sex)?.toString().toLowerCase()
  );
}

function dateOnlyToCustomValuesString(date: Date | undefined | null) {
  if (!date) return undefined;
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

function dateOnlyFromCustomValuesString(date: any | undefined | null) {
  if (date === undefined || date === null) return date;
  return new Date(date);
}

function objectToCustomValues(
  data: any | undefined,
  config: Record<string, IHardcodedField>,
): any {
  if (!data) return undefined;

  const customValues = { ...data.values };
  Object.keys(config).forEach((key) => {
    const field = config[key];
    let value = data[key];
    if (field.fieldType === DataType.DateOnly) {
      value = dateOnlyToCustomValuesString(value);
    } else if (
      field.fieldType === DataType.Dictionary &&
      field.dictionaryName === 'dictionaries.sex_types._title'
    ) {
      value = convertSexToLocalizationKey(value);
    }
    (customValues as any)[field.customValuesFieldName] = value;
  });

  return customValues;
}

/*
 *
 */
function customValuesToObject(
  data: any,
  config: Record<string, IHardcodedField>,
): any {
  const customValues = { ...data };
  const result = { values: customValues };
  Object.keys(config).forEach((key) => {
    const field = config[key];
    if (field.readOnly) {
      delete customValues[field.customValuesFieldName];
      return;
    }

    let value = data[field.customValuesFieldName];
    if (field.fieldType === DataType.DateOnly) {
      value = dateOnlyFromCustomValuesString(value);
    } else if (
      field.fieldType === DataType.Dictionary &&
      field.dictionaryName === 'dictionaries.sex_types._title'
    ) {
      value = convertSexDictionaryIdToEnum(value);
    }
    if (value === '') {
      if (
        field.fieldType === DataType.Dictionary ||
        field.fieldType === DataType.DateOnly ||
        field.fieldType === DataType.DateTime ||
        field.fieldType === DataType.TimeOnly
      ) {
        value = null;
      }
    }
    (result as any)[key] = value;
  });
  return result;
}
